import React, { useState, useEffect } from 'react'
import { Form, Button, Accordion } from 'semantic-ui-react'
import Panel from '../shared/Panel'
import PropTypes from "prop-types";
import _ from 'lodash'
import { useTextResources } from '../../language';
import { onWarning, onError } from '../../components/shared/messages'
import { useLoader } from '../../hooks/useLoader';
import moment from 'moment';

const SearchPanel = ({ options, onSetState, onSearchComplete}) => {
  const text = useTextResources()
  const loader = useLoader()

  const convert = state => {
    const result = { ...state }
    _.each(state, (value, key) => {
      if (_.isDate(value)) {
        result[key] = moment(value).toISOString(true)
      }
    })
    return result
  }

  useEffect(() => {
    if (options.setDoSearchRef) {
      options.setDoSearchRef(doSearch);
    }
  }, [options.setDoSearchRef])

  useEffect(() => {
    if (options.setChangeFieldRef) {
      options.setChangeFieldRef(onChangeField);
    }
  }, [options.setChangeFieldRef])


  const [state, setState] = useState(convert(options.initial) || {})
  const [values, setValues] = useState(options.initial || {})

  useEffect(() => {
    if (options.searchOnLoad) {
      doSearch()
    }
  }, [options.searchOnLoad])

  useEffect(() => {
    if (onSetState) {
      onSetState(state)
    }
  }, [state])

  const onChangeField = (key, value) => {

    const field = options.fields.find(f => f.key === key) || options.advancedFields.find(f => f.key === key)
    if (field && field.props.onChange) {
      field.props.onChange(value);
    }

    const changes = {}
    if (_.isDate(value)) {
      changes[key] = moment(value).toISOString(true)
    }
    else if (_.isPlainObject(value) && options.method !== "POST") {
      _.forEach(value, (value, index) => {
        changes[`${key}.${index}`] = value
      })
    }
    else if (_.isArray(value) && options.method !== "POST") {
      _.forEach(value, (value, index) => {
        if(_.isDate(value)){
            changes[`${key}[${index}]`] = moment(value).toISOString(true)
        } else {
            changes[`${key}[${index}]`] = value
        }
      });
    }
    else if (_.isBoolean(value) || _.isNumber(value)) {
      changes[key] = value
    }
    else {
      changes[key] = value || null
    }

    setState(prev => {
      return _.merge(_.omitBy(prev, (v, k) => k.startsWith(key)), changes)
    })
    setValues(prev => {
      prev[key] = value
      return prev
    })
  }

  // This function is used to set the state of the search panel. It takes an object as an argument and sets the state of the search panel to that object.
  // overrides will override the current state of the search panel. 
  // extra will be added to the current state of the search panel but will not be preserved.
  const doSearch = async (overrides, extra) => {
    if (options.api) {
      try {
        if (extra?.NoLoader !== true) {
          loader(true)
        }
        if (!options.validate || options.validate({ ...state, ...options.spec })) {
          let spec = options.buildSpec?.(state) || { ...state, ...options.spec }
          if (overrides) {
            setValues({...values, ...overrides})
            spec = { ...spec, ...overrides }
            setState(spec)
          }

          if (extra) {
            spec = { ...spec, ...extra }
          }

          const data = await options.api(spec);
          onSearchComplete(data, spec)
        }
      } catch (error) {
        error.status === 400 ? onWarning(error) : onError(error)
      }
      finally {
        if (extra?.NoLoader !== true) {
          loader(false)
        }
      }
    } else if (options.customSearch ) {
      options.customSearch(state)
    }
  }

  return (
    <Panel header={options.title} subheader={options.description}>
      <Form>
        {
          _.chunk(options.fields, options.groupSize || 8).map((chunk, idx) => {
            return <Form.Group key={`Field${idx}`} widths={options.groupSize || 8}>
              {
                chunk.map(field => {
                  return React.cloneElement(field, { value: values[field.key], onChange: value => onChangeField(field.key, value) })
                })
              }
            </Form.Group>
          })
        }
        {
          options.advancedFields &&
          <Accordion panels={[
            {
              title: text("Advanced"),
              key: 'Advanced',
              content: {
                content: _.chunk(options.advancedFields, options.groupSize || 8).map((chunk, idx) => {
                  return <Form.Group key={`AdvField${idx}`} widths={options.groupSize || 8}>
                    {
                      chunk.map(field => {
                        return React.cloneElement(field, { value: values[field.key], onChange: value => onChangeField(field.key, value) })
                      })
                    }
                  </Form.Group>
                })
              }
            }
          ]}>
          </Accordion>
        }
        <Form.Field style={{ textAlign: "right" }}>
          {options.buttons}
          <Button primary type="submit" onClick={()=>doSearch()}>{options.buttonText || text("Search")}</Button>
        </Form.Field>
      </Form>
    </Panel>
  )
}

SearchPanel.propTypes = {
  options: PropTypes.object,
  onSearchComplete: PropTypes.func,
  onSetState: PropTypes.func
}
export default SearchPanel
